import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type { InsuranceProduct } from 'src/interfaces/insuranceProduct'

interface Props {
  isOpen: boolean
  insuranceProduct: InsuranceProduct | null
  isDeleting: boolean
  onConfirm(): void
  onClose(): void
}

const DeleteInsuranceProductModalComponent = ({
  isOpen,
  insuranceProduct,
  isDeleting,
  onConfirm,
  onClose,
}: Props) => {
  if (!insuranceProduct) {
    return null
  }

  return (
    <CommonModal
      isOpen={isOpen}
      title="Delete this insurance product?"
      onClose={onClose}
      buttons={
        <Fragment>
          {/* Disabled while the request is in flight so the control matches the page's
              dismissal guard — a Cancel that looks clickable but does nothing is worse. */}
          <Button size="small" color="grey" disabled={isDeleting} onClick={onClose}>
            Cancel
          </Button>
          {/* `red` is the shared Button's destructive variant, ported from apps/dashboard: a pale
              critical background with critical-500 text. The colour carries the warning
              alongside the title, body and label. */}
          <Button
            size="small"
            color="red"
            loading={isDeleting}
            disabled={isDeleting}
            onClick={onConfirm}
            data-testid="insurance-product-delete-confirm-button"
          >
            Delete product
          </Button>
        </Fragment>
      }
    >
      <div css={tw`flex flex-col gap-2`}>
        <Typography variant="body2">
          {insuranceProduct.name} will be removed from the global catalog.
        </Typography>
        <Typography variant="body2" css={tw`text-shades-500`}>
          A product that a customer policy still uses cannot be deleted — remove it from those
          policies first.
        </Typography>
      </div>
    </CommonModal>
  )
}

export const DeleteInsuranceProductModal = memo(DeleteInsuranceProductModalComponent)
